/**
 * usePermit2Allowance — reads ERC-20 and Permit2 allowance state.
 *
 * Uses multicall to batch both reads in a single RPC round trip.
 * Determines whether ERC-20 approval and/or Permit2 approval are needed.
 *
 * ARCH-27: Contract interaction hook pattern (useQuery with polling)
 * Story 5.2 Task 2
 */

import { useQuery } from "@tanstack/react-query";
import { getPublicClient } from "wagmi/actions";

import { erc20Abi } from "../../lib/abis/erc20";
import { permit2Abi } from "../../lib/abis/permit2";
import { getChainAddresses } from "../../lib/chain-addresses";
import { getActiveChain, wagmiConfig } from "../../lib/wagmi-config";

type Permit2AllowanceData = {
	erc20Allowance: bigint;
	permit2Amount: bigint;
	permit2Expiration: number;
};

function isHex(value: string): value is `0x${string}` {
	return value.startsWith("0x");
}

function toHex(value: string): `0x${string}` {
	const lower = value.toLowerCase();
	if (!isHex(lower)) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error(`Invalid hex address: ${value}`);
	}
	return lower;
}

async function fetchAllowances(
	tokenAddress: string,
	walletAddress: string
): Promise<Permit2AllowanceData> {
	const activeChain = getActiveChain();
	const publicClient = getPublicClient(wagmiConfig, { chainId: activeChain.id });

	if (!publicClient) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error("Public client unavailable");
	}

	const addresses = getChainAddresses(activeChain.id);
	if (!addresses) {
		// eslint-disable-next-line no-restricted-syntax -- TanStack Query queryFn requires thrown errors
		throw new Error(`Unsupported chain: ${activeChain.id}`);
	}

	const tokenHex = toHex(tokenAddress);
	const walletHex = toHex(walletAddress);

	const results = await publicClient.multicall({
		contracts: [
			{
				address: tokenHex,
				abi: erc20Abi,
				functionName: "allowance",
				args: [walletHex, addresses.permit2],
			},
			{
				address: addresses.permit2,
				abi: permit2Abi,
				functionName: "allowance",
				args: [walletHex, tokenHex, addresses.universalRouter],
			},
		],
	});

	const erc20Result = results[0];
	const permit2Result = results[1];

	const erc20Allowance =
		erc20Result.status === "success" ? BigInt(String(erc20Result.result)) : BigInt(0);

	let permit2Amount = BigInt(0);
	let permit2Expiration = 0;

	if (permit2Result.status === "success" && Array.isArray(permit2Result.result)) {
		permit2Amount = BigInt(String(permit2Result.result[0]));
		permit2Expiration = Number(permit2Result.result[1]);
	}

	return { erc20Allowance, permit2Amount, permit2Expiration };
}

type UsePermit2AllowanceReturn = {
	erc20Allowance: bigint | undefined;
	permit2Allowance: { amount: bigint; expiration: number } | undefined;
	needsErc20Approval: boolean;
	needsPermit2Approval: boolean;
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
};

/**
 * Hook to read ERC-20 and Permit2 allowance state.
 *
 * @param tokenAddress - The ERC-20 token contract address
 * @param walletAddress - The connected wallet address
 * @param amountIn - The amount the user wants to spend (for comparison)
 * @returns Allowance data with needs* flags, loading/error states
 */
export function usePermit2Allowance(
	tokenAddress: string,
	walletAddress: string,
	amountIn: bigint
): UsePermit2AllowanceReturn {
	const enabled = Boolean(tokenAddress) && Boolean(walletAddress) && amountIn > BigInt(0);

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["permit2-allowance", tokenAddress, walletAddress],
		queryFn: () => fetchAllowances(tokenAddress, walletAddress),
		enabled,
		refetchInterval: 30_000,
		staleTime: 25_000,
	});

	const needsErc20Approval = data ? data.erc20Allowance < amountIn : false;
	const needsPermit2Approval = data
		? data.permit2Amount < amountIn || data.permit2Expiration < Math.floor(Date.now() / 1000)
		: false;

	return {
		erc20Allowance: data?.erc20Allowance,
		permit2Allowance: data
			? { amount: data.permit2Amount, expiration: data.permit2Expiration }
			: undefined,
		needsErc20Approval,
		needsPermit2Approval,
		isLoading: enabled ? isLoading : false,
		error: error instanceof Error ? error : null,
		refetch,
	};
}
